import React, { ReactNode, useCallback, useMemo } from 'react';
import map from 'lodash/map';
import set from 'lodash/set';

import { ENTER_MANUALLY_OPTION } from './useCondition';
import { SelectOption } from './helpers/memoriesHelper';
import { StyledMemoryDropdown } from './RuleLayout';

export enum MemorySelectorResult {
  ARRAY,
  STRING_WITH_CURLY_BRACKETS,
}

type MemorySelectorProps = {
  attr: string;
  disabled: boolean;
  isEnterManually: boolean;
  fields: string[];
  setCurrentFields: (fields: string[]) => void;
  optGroups: SelectOption[][];
  onChange?: (props: unknown) => void;
  resultType: MemorySelectorResult;
  renderManualInput?: ReactNode;
  notChangeableFields?: string[];
};

const MemoSelector = ({
  attr,
  disabled,
  isEnterManually,
  fields,
  setCurrentFields,
  optGroups,
  onChange,
  resultType: result,
  renderManualInput,
  notChangeableFields,
}: MemorySelectorProps) => {
  const [isManualEditor, setIsManualEditor] = React.useState<boolean>(isEnterManually);

  const handleOnChange = useCallback(
    (value: string, index: number) => {
      const isEnterManually = value === ENTER_MANUALLY_OPTION;

      setIsManualEditor(isEnterManually);

      if (fields[index] === value) {
        return;
      }

      const copyFields = [...fields];
      copyFields[index] = value;

      for (let i = index + 1; i < fields.length; i++) {
        copyFields.pop();
      }

      setCurrentFields(copyFields);

      let res = copyFields as string | string[] | null;

      if (result === MemorySelectorResult.STRING_WITH_CURLY_BRACKETS) {
        res = `{{${copyFields.join('.')}`;

        if (copyFields[copyFields.length - 1]) {
          res += '}}';
        }
      }

      if (res === `{{${ENTER_MANUALLY_OPTION}}}`) {
        res = null;
      }
      onChange && onChange({ currentState: set({}, attr, res) });
    },
    [attr, setCurrentFields, fields, onChange, result]
  );

  const renderFields = useMemo(
    () =>
      isManualEditor
        ? null
        : map(optGroups.slice(1), (group, idx) => {
            const isDisabled = disabled || (notChangeableFields && idx < notChangeableFields.length - 1);

            return (
              <FieldDropdown
                className="memory-selector-dropdown"
                disabled={!!isDisabled}
                index={idx + 1}
                key={idx + 1}
                onChange={handleOnChange}
                options={group}
                value={fields[idx + 1]}
              />
            );
          }),
    [optGroups, disabled, fields, handleOnChange, isManualEditor, notChangeableFields]
  );

  const manualInput = isEnterManually && renderManualInput;

  const disabledFirst = disabled || (notChangeableFields && notChangeableFields.length > 0);

  return (
    <>
      {!isEnterManually && (
        <FieldDropdown
          className="memory-selector-dropdown"
          disabled={disabledFirst}
          index={0}
          key="memorySelector"
          onChange={handleOnChange}
          options={optGroups[0]}
          value={fields[0]}
        />
      )}
      {manualInput}
      {renderFields}
    </>
  );
};

const FieldDropdown = ({
  className,
  disabled,
  index,
  onChange,
  options,
  value,
}: {
  className: string;
  disabled?: boolean;
  index: number;
  onChange: (value: string, index: number) => void;
  options: SelectOption[];
  value: string;
}) => {
  const handleChange = (selectedOption: string) => {
    onChange(selectedOption, index);
  };

  return (
    <StyledMemoryDropdown
      allowEmptyValues
      className={className}
      disabled={disabled}
      fitOpenerWidth={false}
      onChange={handleChange}
      options={options}
      placeholder="Select Memory"
      searchable
      value={value}
    />
  );
};

export default MemoSelector;
